import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api } from '../../lib/api'
import { titleCase } from '../../lib/format'
import type { ListResponse, Site } from '../../types'
import { Alert, Card, CardBody, CardHeader, EmptyState, Skeleton, StatusBadge } from '../../components/ui'
import DownloadButton from '../../components/DownloadButton'
import { IdIcon } from '../../components/icons'

export default function Sites() {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['leader', 'sites'],
    queryFn: () => api.get<ListResponse<Site>>('/sites'),
  })

  const sites = data?.items ?? []

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Mining sites</h1>
          <p className="page-head__sub">
            Sites allocated to your cooperative. New sites are allocated by the administrator.
          </p>
        </div>
        <div className="row">
          <DownloadButton path="/api/v1/leader/exports/sites.xlsx" filename="sites_report.xlsx">
            Export Excel
          </DownloadButton>
          <DownloadButton path="/api/v1/leader/exports/sites.pdf" filename="sites_report.pdf">
            Export PDF
          </DownloadButton>
        </div>
      </div>

      <Card>
        <CardHeader title="Sites" sub={sites.length ? `${sites.length} registered` : undefined} />
        <CardBody flush>
          {isError ? (
            <div style={{ padding: 'var(--sp-6)' }}><Alert tone="danger">{(error as Error).message}</Alert></div>
          ) : isLoading ? (
            <div style={{ padding: 'var(--sp-6)' }} className="stack">
              <Skeleton h={20} /><Skeleton h={20} w="70%" />
            </div>
          ) : sites.length === 0 ? (
            <EmptyState icon={<IdIcon size={22} />} title="No sites allocated yet"
                        body="Mining sites are allocated by the administrator. Once a site is allocated to your organisation it appears here, and you can log production against it." />
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr><th>Site</th><th>Location</th><th>Minerals</th><th>Status</th></tr>
                </thead>
                <tbody>
                  {sites.map((s) => (
                    <tr key={s.id}>
                      <td><Link to={`/sites/${s.id}`} style={{ fontWeight: 600 }}>{s.name}</Link></td>
                      <td>{[titleCase(s.district_id), titleCase(s.region_id)].filter(Boolean).join(', ') || '—'}</td>
                      <td>{(s.mineral_types ?? []).map(titleCase).join(', ') || '—'}</td>
                      <td><StatusBadge status={s.validation_status || 'pending'} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>
    </>
  )
}
